import { Component, OnInit, Input, SimpleChanges, forwardRef } from '@angular/core';
import { KeyValuePipe } from '@angular/common';
import { IonSpinner } from '@ionic/angular/standalone';
import { TranslatePipe } from '@ngx-translate/core';
import { ReactiveFormsModule, ControlContainer, FormGroupDirective, FormControl } from '@angular/forms';
import { Subscription } from 'rxjs';

import { 
    MarvSectionTitleComponent, MarvToggleComponent, MarvSelectComponent, MarvSelectItem, MarvCheckboxComponent,
    MarvItemComponent, MarvSubItemComponent, MarvButtonComponent 
} from '@marv/components';
import { PhotoFiltersValues, PearlsPhotoFiltersValues } from '@app/components';
import { PhotoService } from '@app/services';
import { 
    OperationCard, PearlsPhotoSearchCriteria, PhotoSearchOption, PhotoSearchCriteria, PearlsPhotoProductCategory,
    PearlsPhotoProductRange, PearlsPhotoStoreCheckQuestionStructure, PearlsPhotoStoreCheckQuestionCriteria 
} from '@app/models';

@Component({
    selector: 'app-photo-gallery-filters-pearls',
    templateUrl: './pearls.component.html',
    styleUrls: ['./pearls.component.scss'],
    imports: [
        IonSpinner, TranslatePipe, KeyValuePipe, 
        forwardRef(() => MarvSectionTitleComponent), forwardRef(() => MarvToggleComponent), forwardRef(() => MarvSelectComponent), 
        forwardRef(() => MarvCheckboxComponent), forwardRef(() => MarvItemComponent), forwardRef(() => MarvSubItemComponent), 
        forwardRef(() => MarvButtonComponent)
    ],
    viewProviders: [
        {
            provide: ControlContainer,
            useExisting: FormGroupDirective
        }
    ]
})
export class PhotoGalleryFiltersPearlsComponent  implements OnInit {

    @Input({alias : 'operationsAll', required : true}) operationsAll: Array<OperationCard> = [];
    @Input({alias : 'photoFiltersValues', required : true}) photoFiltersValues: PhotoFiltersValues;

    isFetching: boolean = false;
    operationsSubscription: Subscription;
    operationsPearls: Array<MarvSelectItem> = [];
    photoSearchCriterias: Map<number, PearlsPhotoSearchCriteria> = new Map();

    photoTagAllChecked: Map<number, boolean> = new Map();
    photoProductQuestionAllChecked: Map<number, boolean> = new Map();
    photoQuestionAllChecked: Map<number, boolean> = new Map();
    productCategoryAllChecked: Map<string, boolean> = new Map();
    productRangeAllChecked: Map<string, boolean> = new Map();
    photoPosmQuestionAllChecked: Map<number, boolean> = new Map();
    posmCategoryAllChecked: Map<string, boolean> = new Map();
    posmRangeAllChecked: Map<string, boolean> = new Map();
    storeCheckPhotoAllChecked: Map<number, boolean> = new Map();
    storeCheckGroupAllChecked: Map<string, boolean> = new Map();
    
    constructor(
        public formGroupDirective: FormGroupDirective,
        private photoService: PhotoService
    ) { }

    async ngOnInit() {
    
        await this.loadOperations();        
        
        this.operationsSubscription = this.formGroupDirective.form.controls['operationsIds'].valueChanges.subscribe(async (newValue) => {
            await this.loadOperations();
        });
        
    }
    
    async ngOnChanges(changes: SimpleChanges) {        
        if(changes['photoFiltersValues'] && changes['photoFiltersValues'].currentValue){
            this.formGroupDirective.form.controls['currentPearlsOperationId'].setValue(this.photoFiltersValues.pearlsOperationId, {emitEvent: false});
        }
    }
    
    ngOnDestroy() {
        if(this.operationsSubscription){
            this.operationsSubscription.unsubscribe();
        } 
    }
    
    async loadOperations(){  
        this.isFetching = true;
        
        let operationsIds: Array<number> = [];
        let currentPearlsOperationId: number|null = null;
        let photoSearchCriterias: Map<number, PearlsPhotoSearchCriteria> = new Map();
        let oldPhotoSearchCriterias = this.photoSearchCriterias;
        this.operationsPearls = [];
        
        if(this.formGroupDirective.form.controls['operationsIds'].value){
            this.formGroupDirective.form.controls['operationsIds'].value.forEach((o: string) => {
                let opInfos = o.split('|');
                if(opInfos.length === 2 && opInfos[0] === 'lgg'){
                    operationsIds.push(parseInt(opInfos[1]));
                }
            });
        }
                
        this.operationsAll.forEach(o => {
            if(o.type === 'lgg' && (operationsIds.length === 0 || operationsIds.indexOf(o.id) !== -1)){
                this.operationsPearls.push(new MarvSelectItem(o.name, o.id));
            }            
        });
        
        if(this.operationsPearls.length > 0){
            currentPearlsOperationId = parseInt(this.operationsPearls[0].value.toString());
            for(let op of this.operationsPearls){
                let photoSearchCriteria = await this.photoService.loadPearlsSearchCriteria(parseInt(op.value.toString()));
                if(photoSearchCriteria != null){
                    photoSearchCriterias.set(parseInt(op.value.toString()), photoSearchCriteria);
                }
            }
        }
        
        this.formGroupDirective.form.controls['currentPearlsOperationId'].setValue(currentPearlsOperationId, {emitEvent: false});
        this.setAnimaFiltersControls(photoSearchCriterias, oldPhotoSearchCriterias);        
        this.photoSearchCriterias = photoSearchCriterias;
        
        this.isFetching = false;
    }
    
    setAnimaFiltersControls(newPhotoSearchCriterias: Map<number, PearlsPhotoSearchCriteria>, oldPhotoSearchCriterias: Map<number, PearlsPhotoSearchCriteria>) {         
          
        this.photoTagAllChecked.clear();
        this.photoProductQuestionAllChecked.clear();
        this.photoQuestionAllChecked.clear();
        this.productCategoryAllChecked.clear();
        this.productRangeAllChecked.clear();        
        this.photoPosmQuestionAllChecked.clear();
        this.posmCategoryAllChecked.clear();
        this.posmRangeAllChecked.clear();
        this.storeCheckPhotoAllChecked.clear();
        this.storeCheckGroupAllChecked.clear();
        
        newPhotoSearchCriterias.forEach((photoSearchCriteria, index) => {
            if(!oldPhotoSearchCriterias.has(index)){
                
                this.photoTagAllChecked.set(index, false);
                this.photoProductQuestionAllChecked.set(index, false);
                this.photoQuestionAllChecked.set(index, false);
                this.photoPosmQuestionAllChecked.set(index, false);
                this.storeCheckPhotoAllChecked.set(index, false);
                
                let valuesFilters = this.photoFiltersValues.pearlsPhotoFiltersValues.get(index);
                
                this.formGroupDirective.form.addControl('pearls_enableTaggedPictures_'+index, new FormControl(valuesFilters?.skipTaggedPictures !== true), {emitEvent: false}); 
                this.formGroupDirective.form.addControl('pearls_enableProductPictures_'+index, new FormControl(valuesFilters?.skipProductPictures !== true), {emitEvent: false}); 
                this.formGroupDirective.form.addControl('pearls_enablePosmPictures_'+index, new FormControl(valuesFilters?.skipPosmPictures !== true), {emitEvent: false}); 
                this.formGroupDirective.form.addControl('pearls_enableStoreCheckDataPictures_'+index, new FormControl(valuesFilters?.skipStoreCheckDataPictures !== true), {emitEvent: false}); 
                this.formGroupDirective.form.addControl('pearls_enableRangePictures_'+index, new FormControl(valuesFilters?.skipRangePictures !== true), {emitEvent: false}); 
                this.formGroupDirective.form.addControl('pearls_enableTaskPictures_'+index, new FormControl(valuesFilters?.skipTaskPictures !== true), {emitEvent: false}); 
                this.formGroupDirective.form.addControl('pearls_enableStitchedPhotos_'+index, new FormControl(valuesFilters?.skipStitchedPhotos !== true), {emitEvent: false}); 
                
                if(photoSearchCriteria.photoTagCriteriaChunks?.length > 0){
                    for(let rows of photoSearchCriteria.photoTagCriteriaChunks){
                        for(let tag of rows){
                            let check = valuesFilters?.photoTags && valuesFilters.photoTags.indexOf(tag.id) !== -1;
                            this.formGroupDirective.form.addControl('pearls_photoTag_'+index+'_'+tag.id, new FormControl(check), {emitEvent: false});   
                        }
                    }
                }  
                
                if(photoSearchCriteria.prDmPhotoCriteriaChunks?.length > 0){
                    for(let rows of photoSearchCriteria.prDmPhotoCriteriaChunks){
                        for(let question of rows){
                            let check = valuesFilters?.productQuestions && valuesFilters.productQuestions.indexOf(question.id) !== -1;
                            this.formGroupDirective.form.addControl('pearls_photoProductQuestion_'+index+'_'+question.id, new FormControl(check), {emitEvent: false});   
                        }
                    }
                }                
                
                if(photoSearchCriteria.poDmPhotoCriteriaChunks?.length > 0){
                    for(let rows of photoSearchCriteria.poDmPhotoCriteriaChunks){
                        for(let question of rows){
                            let check = valuesFilters?.posmQuestions && valuesFilters.posmQuestions.indexOf(question.id) !== -1;
                            this.formGroupDirective.form.addControl('pearls_photoPosmQuestion_'+index+'_'+question.id, new FormControl(check), {emitEvent: false});   
                        }
                    }
                }
                
                if(photoSearchCriteria.productCategories && photoSearchCriteria.productCategories.productCategory?.length > 0){
                    for(let category of photoSearchCriteria.productCategories.productCategory){                        
                        this.productCategoryAllChecked.set(index+'_'+category.id, false);
                        this.posmCategoryAllChecked.set(index+'_'+category.id, false);
                        for(let range of category.ranges){     
                            if(category.hasProducts){
                                this.productRangeAllChecked.set(index+'_'+range.id, false);
                                for(let rows of range.productChunks){
                                    for(let product of rows){
                                        let check = valuesFilters?.products && valuesFilters.products.indexOf(product.id) !== -1;
                                        this.formGroupDirective.form.addControl('pearls_photoProduct_'+index+'_'+product.id, new FormControl(check), {emitEvent: false});                                                                             
                                    }
                                }
                            }
                            if(category.hasPosms){
                                this.posmRangeAllChecked.set(index+'_'+range.id, false);
                                for(let rows of range.productChunks){
                                    for(let product of rows){
                                        let check = valuesFilters?.posms && valuesFilters.posms.indexOf(product.id) !== -1;
                                        this.formGroupDirective.form.addControl('pearls_photoPosm_'+index+'_'+product.id, new FormControl(check), {emitEvent: false});
                                    }
                                }
                            }
                        }                        
                    }
                }
                
                if(photoSearchCriteria.scDmPhotoCriteriaChunks && photoSearchCriteria.scDmPhotoCriteriaChunks.length > 0){
                    for(let structure of photoSearchCriteria.scDmPhotoCriteriaChunks){
                        this.storeCheckGroupAllChecked.set(index+'_'+structure.group.id, false);
                        for(let storeCheckData of structure.storeCheckDataModels){  
                            let check = valuesFilters?.storeCheckDataModels && valuesFilters.storeCheckDataModels?.indexOf(storeCheckData.id) !== -1;
                            this.formGroupDirective.form.addControl('pearls_photoStoreCheck_'+index+'_'+storeCheckData.id, new FormControl(check), {emitEvent: false});
                        }
                    }
                }
                
            }
            else{
                oldPhotoSearchCriterias.delete(index);
            }            
        });
        
        oldPhotoSearchCriterias.forEach((photoSearchCriteria, index) => {
            
            this.formGroupDirective.form.removeControl('pearls_skipTaggedPictures_'+index, {emitEvent: false});
            this.formGroupDirective.form.removeControl('pearls_enableProductPictures_'+index, {emitEvent: false}); 
            this.formGroupDirective.form.removeControl('pearls_enablePosmPictures_'+index, {emitEvent: false}); 
            this.formGroupDirective.form.removeControl('pearls_enableStoreCheckDataPictures_'+index, {emitEvent: false}); 
            this.formGroupDirective.form.removeControl('pearls_enableRangePictures_'+index, {emitEvent: false}); 
            this.formGroupDirective.form.removeControl('pearls_enableTaskPictures_'+index, {emitEvent: false}); 
            this.formGroupDirective.form.removeControl('pearls_enableStitchedPhotos_'+index, {emitEvent: false});
                
            if(photoSearchCriteria.photoTagCriteriaChunks?.length > 0){
                for(let rows of photoSearchCriteria.photoTagCriteriaChunks){
                    for(let tag of rows){
                        this.formGroupDirective.form.removeControl('pearls_photoTag_'+index+'_'+tag.id, {emitEvent: false});   
                    }
                }
            }
            
            if(photoSearchCriteria.prDmPhotoCriteriaChunks?.length > 0){
                for(let rows of photoSearchCriteria.prDmPhotoCriteriaChunks){
                    for(let question of rows){
                        this.formGroupDirective.form.removeControl('pearls_photoProductQuestion_'+index+'_'+question.id, {emitEvent: false});   
                    }
                }
            }

            if(photoSearchCriteria.productCategories && photoSearchCriteria.productCategories.productCategory?.length > 0){
                for(let category of photoSearchCriteria.productCategories.productCategory){
                    for(let range of category.ranges){
                        if(category.hasProducts){
                            for(let rows of range.productChunks){
                                for(let product of rows){                                
                                    this.formGroupDirective.form.removeControl('pearls_photoProduct_'+index+'_'+product.id, {emitEvent: false});  
                                } 
                            }
                        }                        
                        if(category.hasPosms){
                            for(let rows of range.posmChunks){
                                for(let product of rows){        
                                    this.formGroupDirective.form.removeControl('pearls_photoPosmQuestion_'+index+'_'+product.id, {emitEvent: false});
                                }
                            }
                        }
                    }
                }
            }
            
            if(photoSearchCriteria.scDmPhotoCriteriaChunks && photoSearchCriteria.scDmPhotoCriteriaChunks.length > 0){
                for(let structure of photoSearchCriteria.scDmPhotoCriteriaChunks){                   
                    for(let storeCheckData of structure.storeCheckDataModels){            
                        this.formGroupDirective.form.removeControl('pearls_photoStoreCheck_'+index+'_'+storeCheckData.id, {emitEvent: false});
                    }
                }
            }
            
        });
    }
    
    toggleCheckVisitPhotos(operationId: number, photoTagCriteriaChunks: Array<Array<PhotoSearchCriteria>>) {
        let check = !(this.photoTagAllChecked.get(operationId) || false);
        for(let rows of photoTagCriteriaChunks){
            for(let tag of rows){
                this.formGroupDirective.form.controls['pearls_photoTag_'+operationId+'_'+tag.id].setValue(check, {emitEvent: false});   
            }
        }
        this.photoTagAllChecked.set(operationId, check);
    }
    
    toggleCheckPhotoProductQuestion(operationId: number, productDataModelsChunks: Array<Array<PhotoSearchCriteria>>) {
        let check = !(this.photoProductQuestionAllChecked.get(operationId) || false);
        for(let rows of productDataModelsChunks){
            for(let question of rows){
                this.formGroupDirective.form.controls['pearls_photoProductQuestion_'+operationId+'_'+question.id].setValue(check, {emitEvent: false}); 
            }
        }
        this.photoProductQuestionAllChecked.set(operationId, check);
    }
    
    toggleCheckPhotoQuestion(operationId: number, productCategories: Array<PearlsPhotoProductCategory>) {
        let check = !(this.photoQuestionAllChecked.get(operationId) || false);
        for(let category of productCategories){
            if(category.hasProducts){
                this.productCategoryAllChecked.set(operationId+'_'+category.id, check);
                for(let range of category.ranges){
                    this.productRangeAllChecked.set(operationId+'_'+range.id, check);
                    for(let rows of range.productChunks){
                        for(let product of rows){                        
                            this.formGroupDirective.form.controls['pearls_photoProduct_'+operationId+'_'+product.id].setValue(check, {emitEvent: false});
                        }
                    }
                }
            }
        }
        this.photoQuestionAllChecked.set(operationId, check);
    }
    
    toggleCheckProductCategory(operationId: number, categoryId: number, ranges: Array<PearlsPhotoProductRange>) {
        let key = operationId+'_'+categoryId;
        let check = !(this.productCategoryAllChecked.get(key) || false);       
        for(let range of ranges){
            this.productRangeAllChecked.set(operationId+'_'+range.id, check);
            for(let rows of range.productChunks){
                for(let product of rows){
                    this.formGroupDirective.form.controls['pearls_photoProduct_'+operationId+'_'+product.id].setValue(check, {emitEvent: false}); 
                }
            }
        }        
        this.productCategoryAllChecked.set(key, check);
    }
    
    toggleCheckProductRange(operationId: number, rangeId: number, productChunks: Array<Array<any>>) {
        let key = operationId+'_'+rangeId;
        let check = !(this.productRangeAllChecked.get(key) || false); 
        for(let rows of productChunks){
            for(let product of rows){
                this.formGroupDirective.form.controls['pearls_photoProduct_'+operationId+'_'+product.id].setValue(check, {emitEvent: false}); 
            }
        }                
        this.productRangeAllChecked.set(key, check);
    }
    
    toggleCheckPhotoPosmQuestion(operationId: number, productDataModelsChunks: Array<Array<PhotoSearchCriteria>>) {
        let check = !(this.photoPosmQuestionAllChecked.get(operationId) || false);
        for(let rows of productDataModelsChunks){
            for(let question of rows){
                this.formGroupDirective.form.controls['pearls_photoPosmQuestion_'+operationId+'_'+question.id].setValue(check, {emitEvent: false}); 
            }
        }
        this.photoPosmQuestionAllChecked.set(operationId, check);
    }    
    
    toggleCheckPhotoPosm(operationId: number, productCategories: Array<PearlsPhotoProductCategory>) {
        let check = !(this.photoPosmQuestionAllChecked.get(operationId) || false);
        for(let category of productCategories){
            if(category.hasPosms){
                this.productCategoryAllChecked.set(operationId+'_'+category.id, check);
                for(let range of category.ranges){
                    this.productRangeAllChecked.set(operationId+'_'+range.id, check);
                    for(let rows of range.posmChunks){
                        for(let product of rows){
                            this.formGroupDirective.form.controls['pearls_photoPosm_'+operationId+'_'+product.id].setValue(check, {emitEvent: false}); 
                        }
                    }
                }
            }
        }
        this.photoPosmQuestionAllChecked.set(operationId, check);
    }
    
    toggleCheckPosmCategory(operationId: number, categoryId: number, ranges: Array<PearlsPhotoProductRange>) {
        let key = operationId+'_'+categoryId;
        let check = !(this.posmCategoryAllChecked.get(key) || false);       
        for(let range of ranges){
            this.productRangeAllChecked.set(operationId+'_'+range.id, check);
            for(let rows of range.posmChunks){
                for(let product of rows){
                    this.formGroupDirective.form.controls['pearls_photoPosm_'+operationId+'_'+product.id].setValue(check, {emitEvent: false}); 
                }
            }
        }        
        this.posmCategoryAllChecked.set(key, check);
    }
    
    toggleCheckPosmRange(operationId: number, rangeId: number, posmChunks: Array<Array<any>>) {
        let key = operationId+'_'+rangeId;
        let check = !(this.posmRangeAllChecked.get(key) || false); 
        for(let rows of posmChunks){
            for(let product of rows){
                this.formGroupDirective.form.controls['pearls_photoPosm_'+operationId+'_'+product.id].setValue(check, {emitEvent: false}); 
            }
        }                
        this.posmRangeAllChecked.set(key, check);
    }
    
    
    toggleCheckStoreCheckPhotos(operationId: number, scDmPhotoCriteriaChunks: Array<PearlsPhotoStoreCheckQuestionStructure>) {
        let check = !(this.storeCheckPhotoAllChecked.get(operationId) || false);
        for(let structure of scDmPhotoCriteriaChunks){
            this.storeCheckGroupAllChecked.set(operationId+'_'+structure.group.id, check);
            for(let storeCheckData of structure.storeCheckDataModels){
                this.formGroupDirective.form.controls['pearls_photoStoreCheck_'+operationId+'_'+storeCheckData.id].setValue(check, {emitEvent: false}); 
            }
        }
        this.storeCheckPhotoAllChecked.set(operationId, check);
    }     
    
    toggleCheckStoreCheckGroup(operationId: number, groupId: number, storeCheckDataModels: Array<PearlsPhotoStoreCheckQuestionCriteria>) {
        let key = operationId+'_'+groupId;
        let check = !(this.storeCheckGroupAllChecked.get(key) || false);
        for(let storeCheckData of storeCheckDataModels){
            this.formGroupDirective.form.controls['pearls_photoStoreCheck_'+operationId+'_'+storeCheckData.id].setValue(check, {emitEvent: false}); 
        }
        this.storeCheckGroupAllChecked.set(key, check);
    } 
}
